import { useEffect, useMemo, useState, type ComponentType } from 'react';
import { Link, Navigate, Route, Routes, useLocation, useNavigate, useParams } from 'react-router-dom';
import {
  Activity,
  ArrowLeft,
  ArrowRight,
  BookOpen,
  Braces,
  Boxes,
  Check,
  ChevronDown,
  ChevronRight,
  CircleDollarSign,
  Cloud,
  Command,
  Database,
  FileText,
  GraduationCap,
  HardDrive,
  Layers3,
  Menu,
  Moon,
  Network,
  Search,
  Server,
  ShieldCheck,
  Sparkles,
  Sun,
  Waypoints,
  X,
} from 'lucide-react';
import {
  articleBySlug,
  articles,
  articlesByTrack,
  searchableText,
  serviceCategories,
  tracks,
  type DocArticle,
  type Track,
  type TrackId,
} from './content';
import { APIOperationPage, APIReferenceHome, APIServicePage } from './api-reference';

const trackIcons: Record<TrackId, ComponentType<{ size?: number; strokeWidth?: number }>> = {
  'get-started': Sparkles,
  training: GraduationCap,
  architecture: Waypoints,
  services: Boxes,
  operations: Activity,
};

const categoryIcons: Record<string, ComponentType<{ size?: number }>> = {
  Compute: Server,
  Networking: Network,
  Storage: HardDrive,
  Data: Database,
  Security: ShieldCheck,
  Platform: Layers3,
};

const panelUrl = 'https://cloud.elysiacloud.com';

function useTheme() {
  const [dark, setDark] = useState(() => {
    const saved = localStorage.getItem('nexus-docs-theme');
    return saved ? saved === 'dark' : window.matchMedia('(prefers-color-scheme: dark)').matches;
  });

  useEffect(() => {
    document.documentElement.dataset.theme = dark ? 'dark' : 'light';
    localStorage.setItem('nexus-docs-theme', dark ? 'dark' : 'light');
  }, [dark]);

  return { dark, setDark };
}

function useProgress() {
  const [completed, setCompleted] = useState<string[]>(() => {
    try {
      return JSON.parse(localStorage.getItem('nexus-docs-progress') ?? '[]');
    } catch {
      return [];
    }
  });

  const toggle = (slug: string) => {
    setCompleted((current) => {
      const next = current.includes(slug) ? current.filter((item) => item !== slug) : [...current, slug];
      localStorage.setItem('nexus-docs-progress', JSON.stringify(next));
      return next;
    });
  };

  return { completed, toggle };
}

function App() {
  const { dark, setDark } = useTheme();
  const progress = useProgress();
  const [mobileNavOpen, setMobileNavOpen] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    setMobileNavOpen(false);
    window.scrollTo({ top: 0, behavior: 'instant' });
  }, [location.pathname]);

  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent) => {
      if ((event.metaKey || event.ctrlKey) && event.key.toLowerCase() === 'k') {
        event.preventDefault();
        setSearchOpen(true);
      }
      if (event.key === 'Escape') {
        setSearchOpen(false);
        setMobileNavOpen(false);
      }
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, []);

  return (
    <div className="app-shell">
      <Header
        dark={dark}
        onTheme={() => setDark(!dark)}
        onMenu={() => setMobileNavOpen(true)}
        onSearch={() => setSearchOpen(true)}
      />
      <div className="workspace">
        <Sidebar completed={progress.completed} className={mobileNavOpen ? 'sidebar mobile-open' : 'sidebar'} />
        {mobileNavOpen && <button className="nav-backdrop" onClick={() => setMobileNavOpen(false)} aria-label="Close navigation" />}
        {mobileNavOpen && (
          <button className="mobile-nav-close icon-button" onClick={() => setMobileNavOpen(false)} aria-label="Close navigation">
            <X size={18} />
          </button>
        )}
        <main className="main-content">
          <Routes>
            <Route path="/" element={<DocsHome completed={progress.completed} />} />
            <Route path="/tracks/:trackId" element={<TrackPage completed={progress.completed} />} />
            <Route path="/docs/:slug" element={<ArticlePage completed={progress.completed} onToggleComplete={progress.toggle} />} />
            <Route path="/api" element={<APIReferenceHome />} />
            <Route path="/api/:section" element={<APIServicePage />} />
            <Route path="/api/:section/:operationId" element={<APIOperationPage />} />
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
        </main>
      </div>
      {searchOpen && <SearchDialog onClose={() => setSearchOpen(false)} />}
    </div>
  );
}

function Header({
  dark,
  onTheme,
  onMenu,
  onSearch,
}: {
  dark: boolean;
  onTheme: () => void;
  onMenu: () => void;
  onSearch: () => void;
}) {
  return (
    <header className="topbar">
      <div className="brand">
        <button className="icon-button mobile-menu" onClick={onMenu} aria-label="Open navigation">
          <Menu size={19} />
        </button>
        <Link to="/" className="brand-link">
          <img src="/elysia-wolf.png" alt="" className="brand-mark" />
          <span>Elysia Nexus</span>
          <span className="brand-divider" />
          <span className="brand-section">Docs</span>
        </Link>
      </div>
      <button className="search-trigger" onClick={onSearch} aria-label="Search Nexus documentation">
        <Search size={16} />
        <span>Search Nexus documentation</span>
        <kbd><Command size={12} /> K</kbd>
      </button>
      <div className="topbar-actions">
        <button className="icon-button" onClick={onTheme} title={dark ? 'Use light mode' : 'Use dark mode'}>
          {dark ? <Sun size={18} /> : <Moon size={18} />}
        </button>
        <a href={panelUrl} className="panel-link">Open Nexus <ArrowRight size={15} /></a>
      </div>
    </header>
  );
}

function Sidebar({ completed, className }: { completed: string[]; className: string }) {
  const location = useLocation();
  const [servicesOpen, setServicesOpen] = useState(true);

  return (
    <aside className={className}>
      <nav aria-label="Documentation navigation">
        <Link className={location.pathname === '/' ? 'nav-link active' : 'nav-link'} to="/">
          <BookOpen size={17} /> Documentation home
        </Link>
        <Link className={location.pathname.startsWith('/api') ? 'nav-link active' : 'nav-link'} to="/api">
          <Braces size={17} /> API reference
        </Link>
        <p className="nav-label">Learning tracks</p>
        {tracks.filter((track) => track.id !== 'services').map((track) => {
          const Icon = trackIcons[track.id];
          const count = articlesByTrack[track.id].filter((article) => completed.includes(article.slug)).length;
          return (
            <Link
              key={track.id}
              className={location.pathname === `/tracks/${track.id}` ? 'nav-link active' : 'nav-link'}
              to={`/tracks/${track.id}`}
            >
              <Icon size={17} />
              <span>{track.title}</span>
              {count > 0 && <span className="nav-count">{count}/{articlesByTrack[track.id].length}</span>}
            </Link>
          );
        })}

        <button className="nav-link nav-toggle" onClick={() => setServicesOpen(!servicesOpen)}>
          <Boxes size={17} />
          <span>Service guides</span>
          {servicesOpen ? <ChevronDown size={15} /> : <ChevronRight size={15} />}
        </button>
        {servicesOpen && (
          <div className="service-nav">
            {serviceCategories.map((category) => {
              const Icon = categoryIcons[category] ?? FileText;
              return (
                <div key={category} className="service-nav-group">
                  <p><Icon size={14} /> {category}</p>
                  {articles
                    .filter((article) => article.track === 'services' && article.category === category)
                    .map((article) => (
                      <Link
                        key={article.slug}
                        to={`/docs/${article.slug}`}
                        className={location.pathname === `/docs/${article.slug}` ? 'service-link active' : 'service-link'}
                      >
                        {completed.includes(article.slug) && <Check size={12} />}
                        {article.title}
                      </Link>
                    ))}
                </div>
              );
            })}
          </div>
        )}
      </nav>
      <div className="sidebar-footer">
        <span>Current Nexus coverage</span>
        <strong>{articles.filter((article) => article.track === 'services').length} service guides</strong>
        <small>Reviewed June 24, 2026</small>
      </div>
    </aside>
  );
}

function DocsHome({ completed }: { completed: string[] }) {
  const startTrack = tracks[0];
  const currentArticle = articlesByTrack['get-started'].find((article) => !completed.includes(article.slug)) ?? articlesByTrack['get-started'][0];

  return (
    <div className="home-page">
      <section className="docs-intro">
        <div>
          <p className="eyebrow">Elysia Nexus documentation</p>
          <h1>Build, understand, and operate your cloud.</h1>
          <p className="lead">Learn Nexus from first principles, follow guided training, or design a production architecture with service-specific guidance.</p>
          <div className="intro-actions">
            <Link to={`/docs/${currentArticle.slug}`} className="primary-button">
              {completed.length ? 'Continue learning' : 'Start learning'} <ArrowRight size={16} />
            </Link>
            <Link to="/tracks/architecture" className="secondary-button">Explore architectures</Link>
          </div>
        </div>
        <div className="coverage-panel">
          <div><strong>{articles.length}</strong><span>articles</span></div>
          <div><strong>{tracks.length}</strong><span>tracks</span></div>
          <div><strong>17</strong><span>systems covered</span></div>
          <div><strong>{completed.length}</strong><span>completed</span></div>
        </div>
      </section>

      <section className="section-block">
        <div className="section-heading">
          <div>
            <p className="eyebrow">Choose a path</p>
            <h2>Learn for your goal</h2>
          </div>
          <p>Tracks are ordered learning paths. Service guides remain available as direct references at every stage.</p>
        </div>
        <div className="track-grid">
          {tracks.filter((track) => track.id !== 'services').map((track, index) => (
            <TrackCard key={track.id} track={track} index={index + 1} completed={completed} />
          ))}
        </div>
      </section>

      <section className="section-band">
        <div className="section-heading">
          <div>
            <p className="eyebrow">Service reference</p>
            <h2>Browse the Nexus platform</h2>
          </div>
          <Link to="/tracks/services" className="text-link">View every service <ArrowRight size={15} /></Link>
        </div>
        <div className="service-directory">
          {serviceCategories.map((category) => {
            const Icon = categoryIcons[category] ?? FileText;
            const entries = articles.filter((article) => article.track === 'services' && article.category === category);
            return (
              <div className="service-directory-group" key={category}>
                <h3><Icon size={18} /> {category}</h3>
                {entries.map((article) => (
                  <Link to={`/docs/${article.slug}`} key={article.slug}>
                    <span>{article.title}</span><ChevronRight size={14} />
                  </Link>
                ))}
              </div>
            );
          })}
        </div>
      </section>

      <section className="start-banner">
        <div>
          <p className="eyebrow">Recommended for new users</p>
          <h2>{startTrack.title}</h2>
          <p>{startTrack.outcome}</p>
        </div>
        <Link to="/tracks/get-started" className="primary-button">Open the track <ArrowRight size={16} /></Link>
      </section>
    </div>
  );
}

function TrackCard({ track, index, completed }: { track: Track; index: number; completed: string[] }) {
  const Icon = trackIcons[track.id];
  const trackArticles = articlesByTrack[track.id];
  const done = trackArticles.filter((article) => completed.includes(article.slug)).length;
  return (
    <Link to={`/tracks/${track.id}`} className="track-card">
      <div className="track-card-top">
        <span className="track-number">{String(index).padStart(2, '0')}</span>
        <Icon size={21} />
      </div>
      <h3>{track.title}</h3>
      <p>{track.description}</p>
      <div className="track-meta">
        <span>{trackArticles.length} lessons</span>
        <span>{done ? `${done} complete` : 'Start track'}</span>
      </div>
      <div className="progress-line"><span style={{ width: `${trackArticles.length ? (done / trackArticles.length) * 100 : 0}%` }} /></div>
    </Link>
  );
}

function TrackPage({ completed }: { completed: string[] }) {
  const { trackId } = useParams();
  const track = tracks.find((item) => item.id === trackId);
  if (!track) return <Navigate to="/" replace />;
  const entries = articlesByTrack[track.id];
  const Icon = trackIcons[track.id];
  const done = entries.filter((article) => completed.includes(article.slug)).length;

  return (
    <div className="track-page content-width">
      <Link to="/" className="back-link"><ArrowLeft size={15} /> Documentation home</Link>
      <header className="track-header">
        <div className="track-icon"><Icon size={24} /></div>
        <div>
          <p className="eyebrow">Learning track</p>
          <h1>{track.title}</h1>
          <p>{track.description}</p>
        </div>
      </header>
      <div className="track-outcome">
        <div><strong>{done}/{entries.length}</strong><span>completed</span></div>
        <p><strong>Outcome:</strong> {track.outcome}</p>
      </div>

      {track.id === 'architecture' && <ArchitecturePlanner />}

      <div className="lesson-list">
        {entries.map((article, index) => (
          <Link to={`/docs/${article.slug}`} className="lesson-row" key={article.slug}>
            <div className={completed.includes(article.slug) ? 'lesson-index complete' : 'lesson-index'}>
              {completed.includes(article.slug) ? <Check size={15} /> : String(index + 1).padStart(2, '0')}
            </div>
            <div className="lesson-copy">
              <div><span>{article.category}</span><span>{article.level}</span><span>{article.minutes} min</span></div>
              <h2>{article.title}</h2>
              <p>{article.summary}</p>
            </div>
            <ArrowRight size={18} />
          </Link>
        ))}
      </div>
    </div>
  );
}

const architectureOptions = {
  workload: ['Public website', 'Internal application', 'API platform', 'Data service'],
  priority: ['Simplicity', 'Availability', 'Security', 'Cost control'],
  data: ['Minimal state', 'Block storage', 'Object storage', 'Managed database'],
};

function ArchitecturePlanner() {
  const [workload, setWorkload] = useState(architectureOptions.workload[0]);
  const [priority, setPriority] = useState(architectureOptions.priority[0]);
  const [data, setData] = useState(architectureOptions.data[0]);

  const recommendation = useMemo(() => {
    const edge = workload === 'Internal application' ? 'Private network and narrow management access' : 'Load balancer, DNS, and TLS';
    const compute = priority === 'Availability' ? 'At least two application instances' : 'One right-sized instance or app deployment';
    const protection = priority === 'Security' ? 'Separate tier security groups and managed secrets' : 'Least-privilege security groups and monitored credentials';
    return [edge, compute, data, protection];
  }, [workload, priority, data]);

  return (
    <section className="planner">
      <div className="planner-heading">
        <div>
          <p className="eyebrow">Architecture starter</p>
          <h2>Shape a reference design</h2>
        </div>
        <Cloud size={26} />
      </div>
      <div className="planner-controls">
        {[
          ['Workload', workload, setWorkload, architectureOptions.workload],
          ['Primary goal', priority, setPriority, architectureOptions.priority],
          ['State model', data, setData, architectureOptions.data],
        ].map(([label, value, setter, options]) => (
          <label key={label as string}>
            <span>{label as string}</span>
            <select value={value as string} onChange={(event) => (setter as (value: string) => void)(event.target.value)}>
              {(options as string[]).map((option) => <option key={option}>{option}</option>)}
            </select>
          </label>
        ))}
      </div>
      <div className="planner-result">
        <strong>Recommended starting shape</strong>
        <ol>{recommendation.map((item) => <li key={item}>{item}</li>)}</ol>
        <small>Use this as a conversation starter, then validate requirements against the detailed patterns below.</small>
      </div>
    </section>
  );
}

function ArticlePage({ completed, onToggleComplete }: { completed: string[]; onToggleComplete: (slug: string) => void }) {
  const { slug } = useParams();
  const article = slug ? articleBySlug.get(slug) : undefined;
  if (!article) return <Navigate to="/" replace />;
  const trackEntries = articlesByTrack[article.track];
  const index = trackEntries.findIndex((entry) => entry.slug === article.slug);
  const previous = index > 0 ? trackEntries[index - 1] : undefined;
  const next = index < trackEntries.length - 1 ? trackEntries[index + 1] : undefined;
  const isComplete = completed.includes(article.slug);

  return (
    <div className="article-layout">
      <article className="article">
        <Link to={`/tracks/${article.track}`} className="back-link"><ArrowLeft size={15} /> {tracks.find((track) => track.id === article.track)?.title}</Link>
        <header className="article-header">
          <div className="article-tags">
            <span>{article.category}</span><span>{article.level}</span><span>{article.minutes} min read</span>
          </div>
          <h1>{article.title}</h1>
          <p>{article.summary}</p>
          <div className="article-freshness">
            <span className="status-dot" /> Reviewed {article.reviewed}
            <span>{article.sourcePaths.length} implementation source{article.sourcePaths.length === 1 ? '' : 's'} tracked</span>
          </div>
        </header>

        <div className="mobile-toc">
          <strong>On this page</strong>
          {article.sections.map((item) => <a key={item.id} href={`#${item.id}`}>{item.title}</a>)}
        </div>

        <div className="article-body">
          {article.sections.map((item) => (
            <section id={item.id} key={item.id}>
              <h2>{item.title}</h2>
              {item.paragraphs?.map((paragraph) => <p key={paragraph}>{paragraph}</p>)}
              {item.bullets && <ul>{item.bullets.map((bullet) => <li key={bullet}>{bullet}</li>)}</ul>}
              {item.steps && <ol>{item.steps.map((step) => <li key={step}>{step}</li>)}</ol>}
              {item.code && <pre><span>{item.code.language}</span><code>{item.code.value}</code></pre>}
              {item.note && (
                <div className={`callout ${item.note.tone}`}>
                  <strong>{item.note.title}</strong>
                  <p>{item.note.body}</p>
                </div>
              )}
            </section>
          ))}
        </div>

        <div className="article-complete">
          <div>
            <strong>{isComplete ? 'Lesson completed' : 'Finished this page?'}</strong>
            <span>Your progress is stored in this browser.</span>
          </div>
          <button className={isComplete ? 'complete-button done' : 'complete-button'} onClick={() => onToggleComplete(article.slug)}>
            <Check size={16} /> {isComplete ? 'Completed' : 'Mark complete'}
          </button>
        </div>

        <nav className="article-pagination">
          {previous ? <Link to={`/docs/${previous.slug}`}><ArrowLeft size={17} /><span><small>Previous</small>{previous.title}</span></Link> : <span />}
          {next ? <Link to={`/docs/${next.slug}`}><span><small>Next</small>{next.title}</span><ArrowRight size={17} /></Link> : <span />}
        </nav>
      </article>

      <aside className="article-aside">
        <p>On this page</p>
        {article.sections.map((item) => <a key={item.id} href={`#${item.id}`}>{item.title}</a>)}
        <div className="source-note">
          <strong>Documentation source</strong>
          <span>Maintained with the Nexus codebase</span>
          <small>{article.reviewed}</small>
        </div>
      </aside>
    </div>
  );
}

function SearchDialog({ onClose }: { onClose: () => void }) {
  const [query, setQuery] = useState('');
  const navigate = useNavigate();
  const results = useMemo(() => {
    const normalized = query.trim().toLowerCase();
    if (!normalized) return articles.slice(0, 8);
    return articles
      .map((article) => {
        const text = searchableText(article);
        const score = (article.title.toLowerCase().includes(normalized) ? 5 : 0)
          + (article.summary.toLowerCase().includes(normalized) ? 3 : 0)
          + (text.includes(normalized) ? 1 : 0);
        return { article, score };
      })
      .filter((result) => result.score > 0)
      .sort((a, b) => b.score - a.score)
      .slice(0, 10)
      .map((result) => result.article);
  }, [query]);

  const open = (article: DocArticle) => {
    onClose();
    navigate(`/docs/${article.slug}`);
  };

  return (
    <div className="search-overlay" onMouseDown={onClose}>
      <div className="search-dialog" onMouseDown={(event) => event.stopPropagation()}>
        <div className="search-input">
          <Search size={18} />
          <input autoFocus value={query} onChange={(event) => setQuery(event.target.value)} placeholder="Search services, concepts, and procedures..." />
          <button onClick={onClose}>Esc</button>
        </div>
        <div className="search-results">
          <p>{query ? `${results.length} results` : 'Suggested documentation'}</p>
          {results.length ? results.map((article) => (
            <button key={article.slug} onClick={() => open(article)}>
              <span className="result-icon"><FileText size={16} /></span>
              <span><strong>{article.title}</strong><small>{article.category} · {article.summary}</small></span>
              <ArrowRight size={15} />
            </button>
          )) : (
            <div className="empty-search"><Search size={24} /><strong>No matching documentation</strong><span>Try a service name, resource type, or task.</span></div>
          )}
        </div>
      </div>
    </div>
  );
}

export default App;
